import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Input, Button } from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { velzonSelectStyles, type VpSelectOption } from '../ui/velzonSelectStyles';
import { VpSelect } from '../ui/VpSelect';
import { PageFilters, FilterSection } from '../ui/PageFilters';

/** Local YYYY-MM-DD (no UTC shift) from a Date. */
function localIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export interface PickListHeadFiltersValue {
  /**
   * Stati visualizzabili. Include sia stati DB grezzi (open, in_progress,
   * fulfilled, confirmed, cancelled, archived) sia il display-status
   * 'assigned' (= open con almeno una mission assegnata). 'assigned' viene
   * gestito post-fetch dal consumer (filtra `display_status === 'assigned'`).
   */
  status: string[];
  external_id_search: string;
  list_type: string | null;
  operator_id: number[];
  list_category: string | null;
  // SP-8b — filtri cliente globali (testo parziale) + EAN + range data
  // prenotazione (testa). Filtrano la LISTA delle pick-list lato backend.
  customer_code: string;
  recipient_code: string;
  customer_name: string;
  ean13: string;
  booking_date_from: string;
  booking_date_to: string;
  // bug_fixes_tpicking_development — pre-filtro "solo evase parzialmente"
  // (card dashboard ?partials=true). Nessun controllo UI: si attiva/azzera via card/Reset.
  partials_only: boolean;
  /**
   * Pre-filtro INVISIBILE come `partials_only`: si attiva solo dal deep-link
   * della card "In attesa di colli" e si azzera col Reset.
   */
  awaiting_colli: boolean;
}

export interface PickListOperatorOption { user_id: number; email: string | null; label: string; }

export interface PickListHeadFiltersProps {
  value: PickListHeadFiltersValue;
  onChange: (next: PickListHeadFiltersValue) => void;
  operators: PickListOperatorOption[];
}

export const DEFAULT_HEAD_FILTERS: PickListHeadFiltersValue = {
  // Default: solo "Ricevuta" (open). L'utente allarga il filtro a mano.
  status: ['open'],
  external_id_search: '', list_type: null, operator_id: [], list_category: null,
  customer_code: '', recipient_code: '', customer_name: '',
  ean13: '', booking_date_from: '', booking_date_to: '', partials_only: false,
  awaiting_colli: false,
};

/**
 * SP-8a — Toolbar filtri pick-list, riadattata per la sidebar verticale.
 * Layout: stack verticale con `<PageFilters>` + sezioni titolate (no piu'
 * Row/Col Bootstrap che non funziona dentro 240px di sidebar).
 *
 * Stati e logica invariati: il filtro 'assigned' continua ad essere un
 * display-status gestito dal consumer (PickListsList).
 */
export function PickListHeadFilters({ value, onChange, operators }: PickListHeadFiltersProps) {
  const { t } = useTranslation(['picklist', 'common']);
  // bug_fixes_tpicking_development — `partials_only` è un pre-filtro INVISIBILE
  // impostato solo dal deep-link della card "Evase parzialmente" (nessun controllo
  // in sidebar). Appena l'utente filtra a mano (stato, cliente, ecc.) va azzerato,
  // altrimenti "il solo parziali" della card si combina silenziosamente con la
  // ricerca appena fatta. Reset a DEFAULT con onChange (il bottone Reset) lo
  // riazzera comunque a false.
  const set = (patch: Partial<PickListHeadFiltersValue>) => onChange({ ...value, partials_only: false, ...patch });

  // Filtro stato: select multipla (react-select isMulti con chip), come in
  // origine. Include 'assigned' (display-status gestito client-side dal consumer).
  const statusOpts: VpSelectOption[] = useMemo(() => [
    { value: 'open',        label: t('common:status.open') },
    { value: 'assigned',    label: t('common:status.assigned') },
    { value: 'in_progress', label: t('common:status.in_progress') },
    { value: 'fulfilled',   label: t('common:status.fulfilled') },
    { value: 'confirmed',   label: t('common:status.confirmed') },
    { value: 'cancelled',   label: t('common:status.cancelled') },
    { value: 'archived',    label: t('common:status.archived') },
  ], [t]);

  const typeOpts: VpSelectOption[] = useMemo(() => [
    { value: 'L', label: t('types.L') },
    { value: 'W', label: t('types.W') },
    { value: 'M', label: t('types.M') },
  ], [t]);

  const categoryOpts: VpSelectOption[] = useMemo(() => [
    { value: 'V', label: t('categories.V') },
    { value: 'S', label: t('categories.S') },
  ], [t]);

  const operatorOpts: VpSelectOption[] = operators.map((o) => ({
    value: o.user_id,
    label: o.label || o.email || `op #${o.user_id}`,
  }));

  // SP-8b — il range data prenotazione vive come due stringhe ISO nello state;
  // flatpickr (range mode) lavora con Date[]: ricostruisco l'array dai bound.
  const dateValue = useMemo<Date[]>(() => {
    if (value.booking_date_from && value.booking_date_to) {
      return [new Date(`${value.booking_date_from}T00:00:00`), new Date(`${value.booking_date_to}T00:00:00`)];
    }
    return [];
  }, [value.booking_date_from, value.booking_date_to]);

  // SP-8g §8 — ordine richiesto: Tipo → Codice cliente → Destinatario →
  // Ragione sociale → Stato → Tipologia → Lista → EAN → Data → Operatore.
  return (
    <PageFilters>
      <FilterSection title={t('filters.type')}>
        <VpSelect
          ariaLabel={t('filters.filter_type_aria')}
          placeholder={t('filters.all_types')}
          isClearable
          options={typeOpts}
          value={value.list_type}
          onChange={(v) => set({ list_type: v == null ? null : String(v) })}
        />
      </FilterSection>

      <FilterSection title={t('filters.customer_code')}>
        <Input
          type="text"
          placeholder={t('filters.customer_code_placeholder')}
          aria-label={t('filters.customer_code_aria')}
          value={value.customer_code}
          onChange={(e) => set({ customer_code: e.target.value })}
        />
      </FilterSection>

      <FilterSection title={t('filters.recipient')}>
        <Input
          type="text"
          placeholder={t('filters.recipient_placeholder')}
          aria-label={t('filters.recipient_aria')}
          value={value.recipient_code}
          onChange={(e) => set({ recipient_code: e.target.value })}
        />
      </FilterSection>

      <FilterSection title={t('filters.customer_name')}>
        <Input
          type="text"
          placeholder={t('filters.customer_name_placeholder')}
          aria-label={t('filters.customer_name_aria')}
          value={value.customer_name}
          onChange={(e) => set({ customer_name: e.target.value })}
        />
      </FilterSection>

      <FilterSection title={t('filters.status')}>
        <Select<VpSelectOption, true>
          isMulti
          options={statusOpts}
          value={statusOpts.filter((o) => value.status.includes(o.value as string))}
          onChange={(opts) => set({ status: opts.map((o) => String(o.value)) })}
          styles={velzonSelectStyles}
          classNamePrefix="vp-select"
          placeholder={t('filters.all_statuses')}
        />
      </FilterSection>

      <FilterSection title={t('filters.item_type')}>
        <VpSelect
          ariaLabel={t('filters.filter_category_aria')}
          placeholder={t('filters.all_categories')}
          isClearable
          options={categoryOpts}
          value={value.list_category}
          onChange={(v) => set({ list_category: v == null ? null : String(v) })}
        />
      </FilterSection>

      <FilterSection title={t('filters.code')}>
        <Input
          type="text"
          placeholder={t('filters.code_search_placeholder')}
          aria-label={t('filters.filter_code_aria')}
          value={value.external_id_search}
          onChange={(e) => set({ external_id_search: e.target.value })}
        />
      </FilterSection>

      <FilterSection title={t('filters.ean13')}>
        <Input
          type="text"
          placeholder={t('filters.ean13_placeholder')}
          aria-label={t('filters.ean13_aria')}
          value={value.ean13}
          onChange={(e) => set({ ean13: e.target.value })}
        />
      </FilterSection>

      <FilterSection title={t('filters.date_range')}>
        <Flatpickr
          className="form-control"
          placeholder={t('filters.date_range')}
          aria-label={t('filters.date_range_aria')}
          value={dateValue}
          onChange={(dates: Date[]) => {
            // Range mode: onChange scatta anche con 1 sola data (1° click) →
            // applichiamo solo a range completo (2). Array vuoto = clear.
            if (dates.length === 2) {
              set({ booking_date_from: localIso(dates[0]), booking_date_to: localIso(dates[1]) });
            } else if (dates.length === 0) {
              set({ booking_date_from: '', booking_date_to: '' });
            }
          }}
          options={{ mode: 'range', dateFormat: 'd/m/Y', locale: { firstDayOfWeek: 1 } }}
        />
      </FilterSection>

      <FilterSection title={t('filters.operator')}>
        <Select<VpSelectOption, true>
          isMulti
          options={operatorOpts}
          value={operatorOpts.filter((o) => value.operator_id.includes(o.value as number))}
          onChange={(opts) => set({ operator_id: opts.map((o) => Number(o.value)) })}
          styles={velzonSelectStyles}
          classNamePrefix="vp-select"
          placeholder={t('filters.all_operators')}
        />
      </FilterSection>

      <Button
        type="button"
        color="secondary"
        outline
        size="sm"
        className="w-100 mt-2"
        onClick={() => onChange(DEFAULT_HEAD_FILTERS)}
        title={t('filters.reset')}
      >
        <i className="bi bi-arrow-counterclockwise me-1" />{t('filters.reset')}
      </Button>
    </PageFilters>
  );
}
